import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "../hooks/useReveal";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

export default function BuiltViewshift() {
  useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="built-viewshift-page">
      <Nav />

      <main className="pt-40 md:pt-48">
        {/* Header */}
        <section className="px-6 md:px-12 pb-16 md:pb-24">
          <div className="max-w-[1400px] mx-auto">
            <Link
              to="/#shipped"
              data-testid="built-back-link"
              className="overline text-slate hover:text-ink transition-colors inline-flex items-center gap-2 mb-12"
            >
              <span>←</span> All shipped work
            </Link>

            <div className="grid grid-cols-12 gap-6 md:gap-10">
              <div className="col-span-12 md:col-span-2">
                <div className="serif text-6xl md:text-7xl font-light text-rule leading-none">
                  01
                </div>
              </div>
              <div className="col-span-12 md:col-span-9 lg:col-span-8">
                <div className="flex items-center gap-3 mb-6">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-sienna opacity-60 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-sienna" />
                  </span>
                  <span className="overline text-ink">Live · viewshift.io</span>
                </div>

                <h1
                  data-testid="built-title"
                  className="serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] font-light tracking-tight leading-[1] text-ink"
                >
                  ViewShift
                </h1>
                <p className="mt-6 serif italic text-2xl md:text-3xl text-moss">
                  YouTube, uncomplicated.
                </p>
                <p className="mt-8 text-lg md:text-xl leading-relaxed text-ink/80 max-w-2xl">
                  An AI-powered YouTube search that ranks results by relevance to
                  your problem — not views. Gives an AI verdict on each video,
                  surfaces the moment that answers your question, and the three
                  comments worth reading.
                </p>

                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://viewshift.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="built-launch"
                    className="inline-flex items-center justify-center gap-3 bg-mustard text-ink px-7 py-4 text-sm tracking-wide hover:bg-ink hover:text-paper transition-colors duration-300 group"
                  >
                    Launch ViewShift
                    <span className="transition-transform duration-300 group-hover:translate-x-1">↗</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Preview image */}
        <section className="px-6 md:px-12 mb-20 md:mb-32">
          <div className="max-w-[1400px] mx-auto bg-sand/40 p-6 md:p-12 lg:p-16">
            <a
              href="https://viewshift.io"
              target="_blank"
              rel="noopener noreferrer"
              className="block max-w-[1080px] mx-auto group reveal"
              aria-label="Open ViewShift"
            >
              <div className="bg-white shadow-[0_40px_80px_-40px_rgba(0,0,0,0.3)] border border-rule transition-transform duration-700 group-hover:-translate-y-1">
                <div className="bg-paper border-b border-rule px-4 py-2.5 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-rule" />
                  <span className="w-3 h-3 rounded-full bg-rule" />
                  <span className="w-3 h-3 rounded-full bg-rule" />
                  <span className="ml-4 mono text-[11px] text-slate truncate">
                    viewshift.io
                  </span>
                </div>
                <img
                  src="/viewshift-preview.jpg"
                  alt="ViewShift product screenshot"
                  className="w-full h-auto block"
                  data-testid="built-hero-image"
                />
              </div>
            </a>
          </div>
        </section>

        {/* The problem / The redesign / What it demonstrates — editorial blocks */}
        <section className="px-6 md:px-12 mb-24 md:mb-32">
          <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-3">
              <div className="overline text-slate reveal">The problem</div>
            </div>
            <div className="col-span-12 md:col-span-8 lg:col-span-7 reveal">
              <p className="serif text-2xl md:text-3xl font-light leading-snug text-ink">
                YouTube has the world&apos;s best how-to content and one of the
                worst ways to find it. The algorithm optimises for watch time
                and clicks — not for whether a video actually answers your
                question.
              </p>
              <p className="mt-6 text-base md:text-lg text-slate leading-relaxed">
                The cost compounds quietly: you watch a 12-minute video that
                buries the answer in minute nine, miss the comment that warns
                the method doesn&apos;t work on your model of car, or never
                find the right video at all because it has 4,000 views instead
                of 4 million.
              </p>
            </div>
          </div>
        </section>

        <div className="rule max-w-[1400px] mx-auto mb-24 md:mb-32" />

        <section className="px-6 md:px-12 mb-24 md:mb-32">
          <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-3">
              <div className="overline text-olive reveal">The redesign</div>
            </div>
            <div className="col-span-12 md:col-span-8 lg:col-span-7 space-y-10 reveal">
              <article>
                <h2 className="serif text-3xl md:text-4xl font-light leading-tight text-ink mb-4">
                  Rerank against the user&apos;s actual problem
                </h2>
                <p className="text-base md:text-lg leading-relaxed text-slate">
                  An LLM reads the candidate video&apos;s title, description,
                  and metadata against the user&apos;s search query and scores
                  it on whether it&apos;s likely to answer the question — not
                  on whether it&apos;s likely to be clicked. View count becomes
                  a feature, not the objective.
                </p>
              </article>
              <article>
                <h2 className="serif text-3xl md:text-4xl font-light leading-tight text-ink mb-4">
                  Pre-verdict before you watch
                </h2>
                <p className="text-base md:text-lg leading-relaxed text-slate">
                  A two-sentence summary and a scorecard tell you whether the
                  video is worth the next ten minutes of your life. If it
                  isn&apos;t, you keep moving without paying the time cost of
                  finding out.
                </p>
              </article>
              <article>
                <h2 className="serif text-3xl md:text-4xl font-light leading-tight text-ink mb-4">
                  Compress the comments into signal
                </h2>
                <p className="text-base md:text-lg leading-relaxed text-slate">
                  YouTube comments are the most under-priced human knowledge
                  on the internet — but they&apos;re buried under hundreds of
                  &ldquo;great video!&rdquo; replies. ViewShift surfaces the
                  three that contain a fix, a warning, or a missing piece, and
                  drops the rest.
                </p>
              </article>
            </div>
          </div>
        </section>

        <div className="rule max-w-[1400px] mx-auto mb-24 md:mb-32" />

        <section className="px-6 md:px-12 mb-24 md:mb-32">
          <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-3">
              <div className="overline text-slate reveal">What it demonstrates</div>
            </div>
            <div className="col-span-12 md:col-span-8 lg:col-span-7 reveal">
              <p className="serif italic text-2xl md:text-3xl font-light leading-snug text-ink">
                AI doesn&apos;t earn its leverage by being added to a workflow.
                It earns its leverage when the workflow is redesigned around
                what AI is actually good at.
              </p>
              <p className="mt-8 text-base md:text-lg text-slate leading-relaxed">
                ViewShift is small enough to ship alone and pointed enough to
                make the principle visible. The same operating logic — find
                where AI compresses work that used to need headcount, redesign
                the workflow around it, drop everything that no longer earns
                its keep — is what I bring to client engagements at scale.
              </p>
            </div>
          </div>
        </section>

        {/* Stack / facts */}
        <section className="px-6 md:px-12 mb-24 md:mb-32 border-y border-rule">
          <div className="max-w-[1400px] mx-auto py-14 md:py-20 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
            <Fact k="Status" v="Live · Free to use" />
            <Fact k="Built by" v="One operator" />
            <Fact k="Login" v="Not required" />
            <Fact k="Data" v="Not stored" />
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 md:px-12 py-20 md:py-32 bg-ink text-paper">
          <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-8">
              <div className="overline text-paper/60 mb-6 reveal">Same thinking, your business</div>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight reveal">
                If you want AI to actually
                <span className="italic text-paper/70"> change the shape</span>
                <br />
                of your operating model —
                <span className="italic text-paper/70"> let&apos;s talk.</span>
              </h2>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal">
                <a
                  href="https://calendly.com/sarangandhi1/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="built-cta-calendly"
                  className="inline-flex items-center justify-center gap-3 bg-mustard text-ink px-7 py-4 text-sm tracking-wide hover:bg-paper hover:text-ink transition-colors duration-300 group"
                >
                  Book a 30-minute call
                  <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </a>
                <Link
                  to="/services/ai-workflow"
                  data-testid="built-cta-service"
                  className="inline-flex items-center justify-center gap-3 text-sm tracking-wide text-paper hover:text-paper/70 transition-colors duration-300 px-2 py-4 border border-paper/30 hover:border-paper/50"
                >
                  See the AI service →
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function Fact({ k, v }) {
  return (
    <div>
      <div className="overline text-slate">{k}</div>
      <div className="mt-2 serif text-xl text-ink font-light">{v}</div>
    </div>
  );
}
